import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { createSmsTemplate, deleteSmsTemplate, getSmsTemplate, listSmsTemplates, patchSmsTemplateMms, SmsTemplate, SmsTemplatePage, updateSmsTemplate } from '../api/smsTemplate';
import { getMmsImage, uploadMmsImage } from '../api/mmsImage';
import { listAllSenderNumbers, sendSmsTemplate } from '../api/solapiSend';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from '../components/common/ConfirmModal';
import Modal from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import PageState from '../components/common/PageState';
import Pagination from '../components/common/Pagination';
import MessagePreviewMockup from '../components/messages/MessagePreviewMockup';
import SolapiConnectionModal from '../components/messages/SolapiConnectionModal';
import { Button } from '../styles/common/Button.styled';
import { ConsoleInput, ConsoleLabel, ConsoleSelect, ConsoleTextArea, FieldError, FormGroup } from '../styles/common/input.styled';
import * as T from '../styles/common/table.styled';
import { getErrorMessage } from '../utils/apiMessage';
import { formatPhoneNumber } from '../utils/format';
import { areSendVariablesFilled, isSendPhoneNumber, normalizeSendNumber } from '../utils/solapiSend';
import { extractSmsTemplateVariables, renderSmsTemplatePreview } from '../utils/smsTemplateVariables';
import { validateMmsImage, validateMmsSubject } from '../utils/mmsImage';

const SearchForm = styled.form`display: flex; align-items: center; gap: 8px; max-width: 420px;`;
const SearchInput = styled(ConsoleInput)`width: 280px;`;
const ContentCell = styled(T.Td)`max-width: 0; white-space: nowrap;`;
const Actions = styled.div`display: flex; align-items: center; gap: 4px;`;
const Help = styled.p`margin: 0 0 16px; color: ${({ theme }) => theme.color.text_color_300}; font-size: 12px; line-height: 18px;`;
const DetectedVariables = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  padding: 12px; border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px;
  background: ${({ theme }) => theme.color.primary_50}; color: ${({ theme }) => theme.color.text_color_200};
  font-size: 12px;
  strong { font-weight: 600; color: ${({ theme }) => theme.color.text_color_100}; }
  div { display: flex; flex-wrap: wrap; gap: 6px; }
`;
const PreviewLayout = styled.div<{ $hasSidebar: boolean }>`
  display: grid; grid-template-columns: ${({ $hasSidebar }) => $hasSidebar ? 'minmax(0, 360px) minmax(0, 1fr)' : 'minmax(0, 1fr)'};
  align-items: start; gap: 20px;
  @media (max-width: 700px) { grid-template-columns: minmax(0, 1fr); }
`;
const PreviewControls = styled.div`display: flex; flex-direction: column; gap: 12px; min-width: 0;`;
const PreviewNote = styled.p`
  margin: 14px 0 0; color: ${({ theme }) => theme.color.text_color_300}; text-align: center; font-size: 12px; line-height: 18px;
`;
const VariableSection = styled.section`
  padding: 12px; border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px;
  background: ${({ theme }) => theme.color.primary_50};
  h3 { margin: 0 0 12px; color: ${({ theme }) => theme.color.text_color_100}; font-size: 13px; font-weight: 600; }
`;
const VariableRow = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) 16px minmax(0, 2fr); align-items: center; gap: 8px;
  & + & { margin-top: 8px; }
  span { color: ${({ theme }) => theme.color.text_color_300}; text-align: center; }
`;
const VariableLabel = styled(ConsoleLabel)`
  color: ${({ theme }) => theme.color.text_color_100}; font-size: 13px; overflow-wrap: anywhere;
`;
const SendSection = styled(VariableSection)`
  h3 { margin-bottom: 16px; }
`;
const SendFields = styled.div`
  display: grid; gap: 12px;
  ${FormGroup} { margin-bottom: 0; }
`;
const SendHelp = styled.p`margin: 8px 0 0; color: ${({ theme }) => theme.color.text_color_300}; font-size: 12px; line-height: 18px;`;
const SendActions = styled.div`display: flex; justify-content: flex-end; margin-top: 16px;`;
const ImagePreview = styled.div`
  display: flex; align-items: center; gap: 12px; margin-top: 10px;
  img { width: 90px; height: 90px; object-fit: contain; border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px; }
  span { overflow-wrap: anywhere; color: ${({ theme }) => theme.color.text_color_200}; font-size: 12px; }
`;
const ImageDropZone = styled.div<{ $dragActive: boolean }>`
  display: grid; grid-template-columns: 24px minmax(0, 1fr) auto; align-items: center; gap: 12px;
  min-height: 68px; box-sizing: border-box; padding: 12px;
  border: 1px dashed ${({ theme, $dragActive }) => $dragActive ? theme.color.point_y_100 : theme.color.primary_400};
  border-radius: 8px; background: ${({ theme }) => theme.color.primary_50};
  color: ${({ theme }) => theme.color.text_color_100};
  svg { width: 24px; height: 24px; color: ${({ theme }) => theme.color.text_color_300}; }
  span { min-width: 0; overflow-wrap: anywhere; font-size: 13px; line-height: 19px; }
  @media (max-width: 420px) { grid-template-columns: 24px minmax(0, 1fr); button { grid-column: 2; justify-self: start; } }
`;
const ImageRequirement = styled.p`
  margin: 8px 0 0; color: ${({ theme }) => theme.color.point_n_100};
  font-size: 13px; font-weight: 600; line-height: 19px;
`;
const RequiredMark = styled.span`color: ${({ theme }) => theme.color.point_n_100}; font-weight: 700;`;

type Editor = { id?: number; name: string; content: string; imageId: string | null; initialImageId: string | null; mmsSubject: string; initialSubject: string; imageFile?: File };
const pageSize = 20;
const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ko-KR');
};

export default function SmsTemplateManagementPage() {
  const { mode, basicAccessToken, basicUser, user } = useAuth();
  const canSend = mode === 'basic' && Boolean((basicUser ?? user)?.permissionList?.includes('SEND_MSG_USE'));
  const canEditConnection = mode === 'basic' && Boolean((basicUser ?? user)?.permissionList?.includes('SEND_MSG_EDIT'));
  const [page, setPage] = useState(1);
  const [searchName, setSearchName] = useState('');
  const [appliedName, setAppliedName] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState<SmsTemplatePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const requestId = useRef(0);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; content?: string; image?: string; mmsSubject?: string }>({});
  const [editorImageUrl, setEditorImageUrl] = useState('');
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [imageValidating, setImageValidating] = useState(false);
  const [imageDragActive, setImageDragActive] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const imageValidationId = useRef(0);
  const [detailPendingId, setDetailPendingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const savePendingRef = useRef(false);
  const [deleteTarget, setDeleteTarget] = useState<SmsTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<SmsTemplate | null>(null);
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [sendTo, setSendTo] = useState('');
  const [sendFrom, setSendFrom] = useState('');
  const [senderNumbers, setSenderNumbers] = useState<string[]>([]);
  const [senderLoading, setSenderLoading] = useState(false);
  const [senderError, setSenderError] = useState(false);
  const [senderReloadKey, setSenderReloadKey] = useState(0);
  const [sendPending, setSendPending] = useState(false);
  const sendPendingRef = useRef(false);
  const detectedVariables = editor ? extractSmsTemplateVariables(editor.content) : [];
  const previewBody = previewTarget ? renderSmsTemplatePreview(previewTarget.content, variables) : '';
  const sendReady = Boolean(previewTarget && canSend && isSendPhoneNumber(sendTo) && senderNumbers.includes(sendFrom) && areSendVariablesFilled(previewTarget.placeholderNames, variables));

  useEffect(() => {
    if (!editor?.imageFile) {
      if (!editor?.imageId) { setEditorImageUrl(''); return; }
      let active = true;
      getMmsImage(editor.imageId).then(image => { if (active) setEditorImageUrl(image.url || ''); }).catch(() => { if (active) setEditorImageUrl(''); });
      return () => { active = false; };
    }
    const url = URL.createObjectURL(editor.imageFile);
    setEditorImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [editor?.imageId, editor?.imageFile]);

  useEffect(() => {
    if (!previewTarget?.imageId) { setPreviewImageUrl(''); return; }
    let active = true;
    setPreviewImageUrl('');
    getMmsImage(previewTarget.imageId).then(image => { if (active) setPreviewImageUrl(image.url || ''); }).catch(() => { if (active) setPreviewImageUrl(''); });
    return () => { active = false; };
  }, [previewTarget?.imageId]);

  useEffect(() => {
    if (!previewTarget || !canSend) return;
    let active = true;
    setSenderLoading(true); setSenderError(false);
    listAllSenderNumbers()
      .then(numbers => { if (active) setSenderNumbers(numbers); })
      .catch(() => { if (active) setSenderError(true); })
      .finally(() => { if (active) setSenderLoading(false); });
    return () => { active = false; };
  }, [previewTarget, canSend, senderReloadKey]);

  useEffect(() => {
    if (mode !== 'basic' || !basicAccessToken) return;
    const id = ++requestId.current;
    setLoading(true);
    setLoadError(false);
    listSmsTemplates({ pg: page, limit: pageSize, ...(appliedName ? { name: appliedName } : {}) })
      .then(data => { if (requestId.current === id) setResult(data); })
      .catch(() => { if (requestId.current === id) setLoadError(true); })
      .finally(() => { if (requestId.current === id) setLoading(false); });
    return () => { requestId.current += 1; };
  }, [mode, basicAccessToken, page, appliedName, reloadKey]);

  const reload = () => setReloadKey(current => current + 1);
  const closeEditor = () => {
    imageValidationId.current += 1;
    setImageValidating(false);
    setImageDragActive(false);
    setEditor(null);
  };
  const search = (event: React.FormEvent) => {
    event.preventDefault();
    const name = searchName.trim();
    if (page === 1 && appliedName === name) reload();
    else { setPage(1); setAppliedName(name); }
  };

  const edit = async (item: SmsTemplate) => {
    if (detailPendingId !== null) return;
    setDetailPendingId(item.id);
    try {
      const detail = await getSmsTemplate(item.id);
      setFieldErrors({});
      setEditor({ id: detail.id, name: detail.name, content: detail.content, imageId: detail.imageId || null, initialImageId: detail.imageId || null, mmsSubject: detail.mmsSubject || '', initialSubject: detail.mmsSubject || '' });
    } catch (error) {
      toast.error(getErrorMessage(error, '템플릿을 불러오지 못했습니다.'));
    } finally {
      setDetailPendingId(null);
    }
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editor || savePendingRef.current || imageValidating) return;
    const name = editor.name.trim();
    const mmsSubject = editor.mmsSubject.trim();
    const errors = {
      name: !name ? '템플릿 이름을 입력해 주세요.' : name.length > 100 ? '이름은 100자 이내로 입력해 주세요.' : undefined,
      content: !editor.content.trim() ? '문자 내용을 입력해 주세요.' : undefined,
      mmsSubject: (editor.imageFile || editor.imageId) && mmsSubject ? validateMmsSubject(mmsSubject) || undefined : undefined,
    };
    setFieldErrors(errors);
    if (errors.name || errors.content || errors.mmsSubject) return;
    savePendingRef.current = true;
    setSaving(true);
    let contentSaved = false;
    try {
      const body = { name, content: editor.content };
      const imageId = editor.imageFile ? (await uploadMmsImage(editor.imageFile)).fileId : editor.imageId;
      if (editor.imageFile && imageId) setEditor(current => current ? { ...current, imageId, imageFile: undefined } : current);
      if (editor.id === undefined) {
        await createSmsTemplate({ ...body, ...(imageId && { imageId, ...(mmsSubject && { mmsSubject }) }) });
      } else {
        await updateSmsTemplate(editor.id, body);
        contentSaved = true;
        if (imageId !== editor.initialImageId || (imageId && mmsSubject !== editor.initialSubject)) {
          await patchSmsTemplateMms(editor.id, imageId ? { imageId, mmsSubject } : { imageId: null });
        }
      }
      toast.success(editor.id === undefined ? '문자 템플릿을 등록했습니다.' : '문자 템플릿을 수정했습니다.');
      closeEditor();
      reload();
    } catch (error) {
      toast.error(contentSaved ? getErrorMessage(error, '본문은 저장되었지만 MMS 설정을 저장하지 못했습니다.') : getErrorMessage(error, '문자 템플릿을 저장하지 못했습니다.'));
    } finally {
      savePendingRef.current = false;
      setSaving(false);
    }
  };

  const chooseImage = async (file?: File) => {
    if (!file || !editor) return;
    const requestId = ++imageValidationId.current;
    setImageValidating(true);
    const error = await validateMmsImage(file);
    if (requestId !== imageValidationId.current) return;
    setImageValidating(false);
    setFieldErrors(current => ({ ...current, image: error || undefined }));
    if (!error) setEditor(current => current ? { ...current, imageFile: file } : current);
  };

  const dropImage = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setImageDragActive(false);
    if (saving || imageValidating) return;
    if (event.dataTransfer.files.length > 1) {
      setFieldErrors(current => ({ ...current, image: '이미지는 한 장만 첨부할 수 있습니다.' }));
      return;
    }
    void chooseImage(event.dataTransfer.files[0]);
  };

  const remove = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await deleteSmsTemplate(deleteTarget.id);
      setDeleteTarget(null);
      toast.success('문자 템플릿을 삭제했습니다.');
      if (result?.content.length === 1 && page > 1) setPage(current => current - 1);
      else reload();
    } catch (error) {
      toast.error(getErrorMessage(error, '문자 템플릿을 삭제하지 못했습니다.'));
    } finally {
      setDeleting(false);
    }
  };

  const openPreview = (item: SmsTemplate) => {
    const values: Record<string, string> = {};
    item.placeholderNames.forEach(name => { values[name] = ''; });
    setVariables(values);
    setSendTo(''); setSendFrom(''); setSenderNumbers([]);
    setPreviewTarget(item);
  };

  const send = async () => {
    if (!previewTarget || !sendReady || sendPendingRef.current) return;
    sendPendingRef.current = true;
    setSendPending(true);
    try {
      await sendSmsTemplate({
        to: normalizeSendNumber(sendTo), from: normalizeSendNumber(sendFrom), smsTemplateId: previewTarget.id,
        ...(previewTarget.placeholderNames.length > 0 && { variables: Object.fromEntries(previewTarget.placeholderNames.map(name => [name, variables[name]])) }),
      });
      toast.success('문자 발송이 접수되었습니다. 최종 전달 상태는 발송 이력에서 확인해 주세요.');
      setPreviewTarget(null);
    } catch (error) {
      toast.error(getErrorMessage(error, '문자를 발송하지 못했습니다.'));
    } finally {
      sendPendingRef.current = false;
      setSendPending(false);
    }
  };

  if (mode !== 'basic' || !basicAccessToken) return <Navigate to="/statistics/calls" replace />;

  return <T.PageContainer>
    <PageHeader title="문자 템플릿 관리" count={!loading && !loadError ? result?.totalElements : undefined} actions={<>
      <Button onClick={() => { setFieldErrors({}); setEditor({ name: '', content: '', imageId: null, initialImageId: null, mmsSubject: '', initialSubject: '' }); }}>새 템플릿</Button>
      {canEditConnection && <Button type="button" $variant="secondary" onClick={() => setConnectionOpen(true)}>SOLAPI 연동</Button>}
    </>} />
    <SearchForm onSubmit={search}>
      <SearchInput aria-label="템플릿 이름 검색" placeholder="템플릿 이름 검색" value={searchName} onChange={event => setSearchName(event.target.value)} />
      <Button type="submit" $variant="secondary">검색</Button>
    </SearchForm>

    {loading ? <PageState status="loading" /> : loadError ? <PageState status="error" onRetry={reload} /> : !result?.content.length ? <PageState status="empty" title={appliedName ? '검색 결과가 없습니다.' : '등록된 문자 템플릿이 없습니다.'} /> : <>
      <T.TableArea $minWidth={840} $stickyActions>
        <T.Table>
          <thead><tr><T.Th $width="19%">템플릿 이름</T.Th><T.Th $width="32%">문자 내용</T.Th><T.Th $width="14%">변수</T.Th><T.Th $width="16%">등록일</T.Th><T.Th $width="230px">관리</T.Th></tr></thead>
          <tbody>{result.content.map(item => <T.Tr key={item.id}>
            <T.Td title={item.name}>{item.name}</T.Td>
            <ContentCell title={item.content}>{item.content}</ContentCell>
            <T.Td title={item.placeholderNames.join(', ')}>{item.placeholderNames.length ? item.placeholderNames.join(', ') : '—'}</T.Td>
            <T.Td>{formatDate(item.createdAt)}</T.Td>
            <T.Td><Actions>
              <T.ActionButton $variant="secondary" onClick={() => openPreview(item)}>미리보기</T.ActionButton>
              <T.ActionButton $variant="secondary" disabled={detailPendingId !== null} onClick={() => void edit(item)}>수정</T.ActionButton>
              <T.ActionButton $variant="danger" onClick={() => setDeleteTarget(item)}>삭제</T.ActionButton>
            </Actions></T.Td>
          </T.Tr>)}</tbody>
        </T.Table>
      </T.TableArea>
      <T.TableFooter>
        <T.TotalCount>총 <span>{result.totalElements.toLocaleString()}</span>건</T.TotalCount>
        <Pagination currentPage={page} totalPages={result.totalPages} onPageChange={setPage} />
      </T.TableFooter>
    </>}

    {editor && <Modal title={editor.id === undefined ? '문자 템플릿 등록' : '문자 템플릿 수정'} onClose={closeEditor} closeDisabled={saving} historyMode="none" width={520} footer={<>
      <Button type="button" $variant="secondary" disabled={saving} onClick={closeEditor}>취소</Button>
      <Button type="submit" form="sms-template-form" disabled={saving || imageValidating}>{saving ? '저장 중...' : editor.id === undefined ? '등록' : '저장'}</Button>
    </>}>
      <form id="sms-template-form" onSubmit={save}>
        <FormGroup>
          <ConsoleLabel htmlFor="sms-template-name">템플릿 이름 <RequiredMark aria-hidden="true">*</RequiredMark></ConsoleLabel>
          <ConsoleInput id="sms-template-name" aria-label="템플릿 이름" maxLength={100} value={editor.name} onChange={event => setEditor({ ...editor, name: event.target.value })} aria-invalid={Boolean(fieldErrors.name)} aria-describedby={fieldErrors.name ? 'sms-template-name-error' : undefined} />
          {fieldErrors.name && <FieldError id="sms-template-name-error" role="alert">{fieldErrors.name}</FieldError>}
        </FormGroup>
        <FormGroup>
          <ConsoleLabel htmlFor="sms-template-content">문자 내용 <RequiredMark aria-hidden="true">*</RequiredMark></ConsoleLabel>
          <ConsoleTextArea id="sms-template-content" aria-label="문자 내용" rows={6} placeholder="예: 안녕하세요, #{이름}님. #{예약일}에 뵙겠습니다." value={editor.content} onChange={event => setEditor({ ...editor, content: event.target.value })} aria-invalid={Boolean(fieldErrors.content)} aria-describedby={fieldErrors.content ? 'sms-template-content-error sms-template-variable-help' : 'sms-template-variable-help'} />
          {fieldErrors.content && <FieldError id="sms-template-content-error" role="alert">{fieldErrors.content}</FieldError>}
        </FormGroup>
        <Help id="sms-template-variable-help">변수는 <code>{'#{변수명}'}</code> 형식으로 입력하세요. 예: <code>{'안녕하세요, #{이름}님.'}</code> 발송 시 변수 이름에 해당하는 실제 값을 넣습니다.</Help>
        <DetectedVariables role="status" aria-label="감지된 변수" aria-live="polite">
          <strong>감지된 변수</strong>
          {detectedVariables.length ? <div>{detectedVariables.map(name => <T.Badge key={name} $tone="neutral">{`#{${name}} → ${name}`}</T.Badge>)}</div> : <span>감지된 변수가 없습니다.</span>}
        </DetectedVariables>
        <FormGroup style={{ marginTop: 16 }}>
          <ConsoleLabel htmlFor="sms-template-image">MMS 이미지 (선택)</ConsoleLabel>
          <input id="sms-template-image" ref={imageInputRef} type="file" accept="image/jpeg" style={{ display: 'none' }} onChange={event => { void chooseImage(event.target.files?.[0]); event.target.value = ''; }} aria-describedby="sms-template-image-help" />
          <ImageDropZone $dragActive={imageDragActive} onDragOver={event => { event.preventDefault(); if (!saving && !imageValidating) setImageDragActive(true); }} onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setImageDragActive(false); }} onDrop={dropImage}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 15V4m0 0L8 8m4-4 4 4M4 13v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <span>{editor.imageFile || editor.imageId ? '이미지가 선택되었습니다. 다른 JPG를 놓아 교체할 수 있습니다.' : 'JPG 이미지를 이곳에 끌어놓거나 파일을 선택하세요.'}</span>
            <Button type="button" $variant="secondary" $size="sm" disabled={saving || imageValidating} onClick={() => imageInputRef.current?.click()}>{editor.imageFile || editor.imageId ? '파일 변경' : '파일 선택'}</Button>
          </ImageDropZone>
          <ImageRequirement id="sms-template-image-help">필수규격: JPG 1장 · 200KB 이하 · 1500×1440px 이하.</ImageRequirement>
          {fieldErrors.image && <FieldError role="alert">{fieldErrors.image}</FieldError>}
          {(editor.imageFile || editor.imageId) && <>
            <ImagePreview>{editorImageUrl && <img src={editorImageUrl} alt="첨부 이미지 미리보기" />}<span>{editor.imageFile?.name || editor.imageId}</span></ImagePreview>
            <Button type="button" $variant="ghost" $size="sm" onClick={() => { setEditor({ ...editor, imageId: null, imageFile: undefined, mmsSubject: '' }); setFieldErrors(current => ({ ...current, image: undefined, mmsSubject: undefined })); }}>이미지 제거</Button>
          </>}
        </FormGroup>
        {(editor.imageFile || editor.imageId) && <FormGroup>
          <ConsoleLabel htmlFor="sms-template-subject">MMS 제목</ConsoleLabel>
          <ConsoleInput id="sms-template-subject" value={editor.mmsSubject} onChange={event => setEditor({ ...editor, mmsSubject: event.target.value })} aria-invalid={Boolean(fieldErrors.mmsSubject)} />
          <Help>40byte 이하이며 변수는 사용할 수 없습니다.</Help>
          {fieldErrors.mmsSubject && <FieldError role="alert">{fieldErrors.mmsSubject}</FieldError>}
        </FormGroup>}
      </form>
    </Modal>}

    {deleteTarget && <ConfirmModal title="문자 템플릿 삭제" message={`“${deleteTarget.name}” 템플릿을 삭제하시겠습니까?\n삭제된 템플릿은 복구할 수 없습니다.`} confirmLabel="삭제" tone="danger" pending={deleting} onCancel={() => setDeleteTarget(null)} onConfirm={() => void remove()} />}

    {previewTarget && <Modal title="문자 미리보기" onClose={() => setPreviewTarget(null)} closeDisabled={sendPending} historyMode="none" width={previewTarget.placeholderNames.length || canSend ? 840 : 520} footer={<>
      <Button type="button" $variant="secondary" disabled={sendPending} onClick={() => setPreviewTarget(null)}>닫기</Button>
    </>}>
      <PreviewLayout $hasSidebar={previewTarget.placeholderNames.length > 0 || canSend}>
        <MessagePreviewMockup variant="sms" text={previewBody} imageUrl={previewImageUrl} subject={previewTarget.mmsSubject} showWebSender />
        {(previewTarget.placeholderNames.length > 0 || canSend) && <PreviewControls>
          {previewTarget.placeholderNames.length > 0 && <VariableSection aria-labelledby="sms-preview-variables-title">
            <h3 id="sms-preview-variables-title">변수 입력</h3>
            {previewTarget.placeholderNames.map(name => <VariableRow key={name}>
              <VariableLabel htmlFor={`sms-preview-${name}`}>{`#{${name}}`}</VariableLabel>
              <span aria-hidden="true">→</span>
              <ConsoleInput id={`sms-preview-${name}`} placeholder={`${name} 입력`} value={variables[name] || ''} onChange={event => setVariables(current => ({ ...current, [name]: event.target.value }))} />
            </VariableRow>)}
          </VariableSection>}
          {canSend && <SendSection aria-labelledby="sms-preview-send-title">
            <h3 id="sms-preview-send-title">발송 정보</h3>
            <SendFields>
              <FormGroup><ConsoleLabel htmlFor="sms-send-to">수신 번호</ConsoleLabel><ConsoleInput id="sms-send-to" type="tel" inputMode="tel" placeholder="010-1234-5678" value={sendTo} onChange={event => setSendTo(event.target.value)} /></FormGroup>
              <FormGroup><ConsoleLabel htmlFor="sms-send-from">발신 번호</ConsoleLabel><ConsoleSelect id="sms-send-from" value={sendFrom} disabled={senderLoading || senderNumbers.length === 0} onChange={event => setSendFrom(event.target.value)}>
                <option value="">{senderLoading ? '불러오는 중...' : '선택해 주세요'}</option>
                {senderNumbers.map(number => <option key={number} value={number}>{formatPhoneNumber(number)}</option>)}
              </ConsoleSelect></FormGroup>
            </SendFields>
            {senderError && <SendHelp role="alert">발신번호를 불러오지 못했습니다. <Button type="button" $variant="ghost" $size="sm" onClick={() => setSenderReloadKey(key => key + 1)}>다시 시도</Button></SendHelp>}
            {!senderLoading && !senderError && senderNumbers.length === 0 && <SendHelp>사용 가능한 발신번호가 없습니다.</SendHelp>}
            <SendHelp>수신번호는 숫자 8~15자리로 입력해 주세요. 발송 접수 후 전달 결과는 발송 이력에서 확인할 수 있습니다.</SendHelp>
            <SendActions><Button type="button" disabled={!sendReady || sendPending} onClick={() => void send()}>{sendPending ? '발송 중...' : '발송'}</Button></SendActions>
          </SendSection>}
        </PreviewControls>}
      </PreviewLayout>
      <PreviewNote>예시 미리보기는 실제 발송된 내용과 다를 수 있습니다.</PreviewNote>
    </Modal>}
    {connectionOpen && canEditConnection && <SolapiConnectionModal onClose={() => setConnectionOpen(false)} />}
  </T.PageContainer>;
}
